import type { CycleDetailsItems } from '@/api';
import { useAppDispatch } from '@/app/store';
import { ProductImage } from '@/components/layout/ProductImage';
import { UIBox } from '@/components/ui/Box';
import { Typography } from '@/components/ui/Typography';
import { useCycleCountContext } from '@/context/cycleCount';
import {
  setDecodedItemsConfirmed,
  setDecodedItemsNotConfirmed,
} from '@/features/cycleCount/cycleCountSlice';
import { useSelector } from '@/hooks/useSelector';
import React, { useCallback, useState } from 'react';

import {
  StyledAccordion,
  StyledAccordionItemContent,
  StyledContentInfo,
  StyledDecodedAccordionContent,
  StyledDecodedCheckbox,
  StyledDecodedProductContent,
  StyledDescription,
  StyledProductItem,
} from '../../listItemStyle';
import { ProductListItemProps } from '../types';

export const DecodedProductListItem: React.FC<
  ProductListItemProps & CycleDetailsItems
> = ({ type, disableActions, ...product }) => {
  const dispatch = useAppDispatch();

  const { setProductDetailsByUpc, setProductModalVisibility } =
    useCycleCountContext();
  const { decodedItemsConfirmed, decodedItemsNotConfirmed } = useSelector(
    state => state.cycleCount.products
  );

  const [accordionExpanded, setAccordionExpanded] = useState<boolean>(true);

  const accordionClickHandler = (): void => setAccordionExpanded(p => !p);

  //#region - COMMON FUNCTIONS
  const handleProductImageClick = (): void => {
    setProductDetailsByUpc(product.upcCode);
    setProductModalVisibility(true);
  };

  const hasEpc = (
    list: CycleDetailsItems[],
    upcCode: string,
    epcCode: string
  ): boolean =>
    list.some(
      item =>
        item.upcCode === upcCode &&
        item.epcCodes.some(epc => epc.epcCode === epcCode)
    );

  const isEpcChecked = useCallback(
    (epcCode: string): boolean =>
      hasEpc(decodedItemsConfirmed ?? [], product.upcCode, epcCode),
    [decodedItemsConfirmed, product.upcCode]
  );

  /**
   * Sposta un epc da una lista all'altra in modo immutabile.
   * - rimuove l'epc dalla lista di partenza
   * - elimina l'upc dalla lista di partenza se rimane senza epcCodes
   * - aggiunge l'epc all'upc di destinazione, creandolo se non esiste
   */
  const moveEpc = (
    from: CycleDetailsItems[],
    to: CycleDetailsItems[],
    epcCode: string
  ): { from: CycleDetailsItems[]; to: CycleDetailsItems[] } => {
    const sourceItem = from.find(item => item.upcCode === product.upcCode);
    const epcItem = sourceItem?.epcCodes.find(epc => epc.epcCode === epcCode);

    if (!sourceItem || !epcItem) {
      return { from, to };
    }

    const nextFrom = from
      .map(item =>
        item.upcCode === product.upcCode
          ? {
            ...item,
            epcCodes: item.epcCodes.filter(epc => epc.epcCode !== epcCode),
          }
          : item
      )
      .filter(item => item.epcCodes.length > 0);

    const targetExists = to.some(item => item.upcCode === product.upcCode);

    const nextTo = targetExists
      ? to.map(item =>
        item.upcCode === product.upcCode
          ? { ...item, epcCodes: [...item.epcCodes, epcItem] }
          : item
      )
      : [...to, { ...product, epcCodes: [epcItem] }];

    return { from: nextFrom, to: nextTo };
  };

  const handleEpcCheckboxClick = (epcCode: string): void => {
    const confirmed = decodedItemsConfirmed ?? [];
    const notConfirmed = decodedItemsNotConfirmed ?? [];

    // UNCHECK: epc confermato -> passa nei non confermati
    if (hasEpc(confirmed, product.upcCode, epcCode)) {
      const { from, to } = moveEpc(confirmed, notConfirmed, epcCode);

      dispatch(setDecodedItemsConfirmed({ decodedItemsConfirmed: from }));
      dispatch(setDecodedItemsNotConfirmed({ decodedItemsNotConfirmed: to }));
      return;
    }

    // CHECK: epc non confermato -> passa nei confermati
    if (hasEpc(notConfirmed, product.upcCode, epcCode)) {
      const { from, to } = moveEpc(notConfirmed, confirmed, epcCode);

      dispatch(setDecodedItemsNotConfirmed({ decodedItemsNotConfirmed: from }));
      dispatch(setDecodedItemsConfirmed({ decodedItemsConfirmed: to }));
      return;
    }

    // CHECK: epc non presente in nessuna delle due liste (stato iniziale)
    const epcItem = product.epcCodes.find(epc => epc.epcCode === epcCode);

    if (!epcItem) {
      return;
    }

    const upcIndex = confirmed.findIndex(
      item => item.upcCode === product.upcCode
    );

    const nextConfirmed =
      upcIndex >= 0
        ? confirmed.map((item, index) =>
          index === upcIndex
            ? { ...item, epcCodes: [...item.epcCodes, epcItem] }
            : item
        )
        : [...confirmed, { ...product, epcCodes: [epcItem] }];

    dispatch(
      setDecodedItemsConfirmed({ decodedItemsConfirmed: nextConfirmed })
    );
  };
  //#endregion

  return (
    <StyledProductItem>
      <StyledDecodedProductContent $type={type}>
        <StyledContentInfo>
          {product.epcCodes.length > 1 && (
            <StyledAccordion
              expanded={accordionExpanded}
              onClick={accordionClickHandler}
            />
          )}
          <ProductImage
            imageUrl={product.imageUrl}
            colorCode={product.colorCode}
            modelCode={product.modelCode}
            brandCode={product.brandCode}
            onClick={handleProductImageClick}
          />
          <UIBox flexDirection="column">
            <StyledDescription>
              <UIBox alignItems="center">
                <Typography size="lg" font="heavy" as="span">
                  {product.brandCode || product.brandDescription} -{' '}
                  {product.brandDescription} - {product.styleName}
                </Typography>
              </UIBox>
              <Typography margin="8px 0 0 0" size="md" font="book" as="span">
                {product.upcCode} - {product.modelCode} - {product.commodity}
              </Typography>
            </StyledDescription>
          </UIBox>
        </StyledContentInfo>
      </StyledDecodedProductContent>
      {product.epcCodes.length > 0 && accordionExpanded && (
        <StyledDecodedAccordionContent ml="0px">
          {product.epcCodes.map(({ epcCode }) => (
            <StyledAccordionItemContent key={epcCode}>
              <UIBox alignItems="center">
                <StyledDecodedCheckbox
                  checked={isEpcChecked(epcCode)}
                  disabled={disableActions}
                  onClick={(): void => handleEpcCheckboxClick(epcCode)}
                />
                <UIBox alignItems="center">
                  <Typography margin="0 0 0 12px" color="grey">
                    {epcCode}
                  </Typography>
                </UIBox>
              </UIBox>
            </StyledAccordionItemContent>
          ))}
        </StyledDecodedAccordionContent>
      )}
    </StyledProductItem>
  );
};

export default DecodedProductListItem;
